import { clientDasboardRoute, clientsRoute, fundDDQRunsRoute } from '@shared/shared-routes';

export const clientDdqInvitesRoute = 'ddq-invites';
export const clientMarketplaceRoute = 'marketplace';

/** Dashboard tabs that host nested `/:productId/ddq` product shells. */
export const clientDashboardDdqProductTabRoutes = [
  fundDDQRunsRoute,
  clientDdqInvitesRoute,
  clientMarketplaceRoute,
] as const;

export interface ClientNavigationContext {
  clientId: string;
  isClientUserContext: boolean;
}

export function extractClientContextFromUrl(url: string): ClientNavigationContext | null {
  const pathOnly = (url ?? '').split('?')[0];

  const clientsDashboardMatch = pathOnly.match(/^\/clients\/([^/]+)\/dashboard(?:\/|$)/);
  if (clientsDashboardMatch?.[1]) {
    return { clientId: clientsDashboardMatch[1], isClientUserContext: false };
  }

  const clientUserMatch = pathOnly.match(/^\/client\/([^/]+)(?:\/|$)/);
  if (clientUserMatch?.[1]) {
    return { clientId: clientUserMatch[1], isClientUserContext: true };
  }

  return null;
}

/**
 * Resolve client_company for Keep Private.
 * Priority: explicit form value → logged-in client user uid → LP client dashboard URL.
 * Note: `/client/{segment}/…` often uses a product uid, so client-user URL segments are not trusted.
 */
export function resolveClientCompanyForKeepPrivate(
  keepPrivate: boolean,
  clientCompany: string | number | null | undefined,
  routerUrl: string,
  loggedInClientUid?: string | null,
): string | number | null {
  if (!keepPrivate) {
    return null;
  }
  if (clientCompany != null && clientCompany !== '') {
    return clientCompany;
  }
  if (loggedInClientUid != null && loggedInClientUid !== '') {
    return loggedInClientUid;
  }

  const clientContext = extractClientContextFromUrl(routerUrl);
  if (!clientContext || clientContext.isClientUserContext) {
    return null;
  }
  return clientContext.clientId;
}

export function buildFundDdqViewQueryParams(issuerUid: string): Record<string, string> {
  return {
    context: 'fund-ddq-runs',
    viewAs: 'investor',
    issuerUid,
  };
}

/** Clears legacy fund-DDQ query params from client DDQ shell URLs (context lives in services). */
export function fundDdqQueryParamsToClear(): Record<string, null> {
  return {
    context: null,
    viewAs: null,
    issuerUid: null,
    issuerid: null,
  };
}

/**
 * Client `/clients/.../ddq` and `/client/.../ddq` shells use {@link OrganizationContextService} +
 * {@link ClientDdqScopeCacheService}; only global `/product/...` routes need query params.
 */
export function fundDdqNavigationExtras(
  clientContext: ClientNavigationContext | null,
  issuerUid: string,
): { queryParams?: Record<string, string> } {
  if (clientContext) {
    return {};
  }
  return { queryParams: buildFundDdqViewQueryParams(issuerUid) };
}

/**
 * In-app route that keeps the client shell (dashboard tabs or client-user layout).
 * Returns null when the caller should use the global `/product/.../data-room` route.
 */
export function buildClientDdqDataroomRoute(
  clientContext: ClientNavigationContext | null,
  productUid: string,
  dashboardTabRoute: string = fundDDQRunsRoute,
): string[] | null {
  if (!clientContext) {
    return null;
  }

  if (!clientContext.isClientUserContext) {
    return [
      '/',
      clientsRoute,
      clientContext.clientId,
      clientDasboardRoute,
      dashboardTabRoute,
      productUid,
      'ddq',
      'data-room',
    ];
  }
  return ['/', 'client', productUid, 'ddq', 'data-room'];
}

export function buildFundDdqDataroomRoute(
  clientContext: ClientNavigationContext | null,
  productUid: string,
): string[] | null {
  return buildClientDdqDataroomRoute(clientContext, productUid, fundDDQRunsRoute);
}

export function isClientDashboardDdqTabRoute(segment: string): boolean {
  return (clientDashboardDdqProductTabRoutes as readonly string[]).includes(segment);
}

/**
 * Advisor client-dashboard marketplace DDQ (keeps headline + header nav).
 * True client-user marketplace still uses `/client/{productUid}/ddq`.
 *
 * Advisor URLs intentionally omit the DDQ run id segment so the path matches
 * Fund Information tab navigation and fund-ddq-runs (`…/ddq/due-diligence`).
 */
export function buildMarketplaceDdqRoute(
  clientContext: ClientNavigationContext | null,
  productUid: string,
  primaryDdqUid?: string | null,
): string[] | null {
  if (!clientContext) {
    return null;
  }

  if (!clientContext.isClientUserContext) {
    return [
      '/',
      clientsRoute,
      clientContext.clientId,
      clientDasboardRoute,
      clientMarketplaceRoute,
      productUid,
      'ddq',
      'due-diligence',
    ];
  }

  const commands = ['/', 'client', productUid, 'ddq'];
  if (primaryDdqUid) {
    commands.push(primaryDdqUid);
  }
  return commands;
}
